'use client';

import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import {
  createWaypoint,
  errorIndices,
  estimateFlightTimeS,
  insertWaypointAt,
  midpoint,
  moveWaypoint,
  removeWaypointAt,
  routeDistanceM,
  updateWaypointAt,
  validateRoute,
  type KeepInFence,
  type LatLng,
  type PlanWaypoint,
  type RouteViolation,
} from '@/lib/mission-planning';

/** Offset (deg) used to seed a form-added waypoint next to the previous one. */
const ADD_OFFSET_DEG = 0.001;
/** Fallback seed when neither a home nor an existing waypoint is available. */
const FALLBACK_SEED: LatLng = { lat: 32.08, lng: 34.78 };
const DEFAULT_SEED_ALTITUDE = 50;

/**
 * NOTE: "is the map armed for this plan" is deliberately NOT in here.
 *
 * That bit is the COP's one map mode (`lib/map-mode.ts`), because it has to be
 * arbitrated against every other armed interaction — a dispatch pick, a zone
 * draw, a mission compose. Holding a second copy of it in this reducer is what
 * allowed two modes to be armed at once and a click to mean two things. The
 * hook is told whether it holds the map (`armed`) and asks for that to change
 * (`onArmedChange`); it never decides on its own.
 *
 * `planningKind` stays — that is which KIND of plan is being drawn, which is
 * this feature's business and means nothing to the map.
 */
interface PlanState {
  waypoints: PlanWaypoint[];
  selectedIndex: number | null;
  planningKind: 'idle' | 'point' | 'route';
  seedAltitude: number;
}

const INITIAL_PLAN: PlanState = {
  waypoints: [],
  selectedIndex: null,
  planningKind: 'idle',
  seedAltitude: DEFAULT_SEED_ALTITUDE,
};

type PlanAction =
  | { type: 'RESET' }
  | { type: 'CLEAR' }
  | { type: 'FINISH_PLANNING' }
  | { type: 'REPLACE'; waypoints: PlanWaypoint[] }
  | { type: 'ADD'; lat: number; lng: number }
  | { type: 'ADD_NEXT'; home: LatLng | null }
  | { type: 'INSERT_AFTER'; index: number; home: LatLng | null }
  | { type: 'UPDATE'; index: number; patch: Partial<PlanWaypoint> }
  | { type: 'REMOVE'; index: number }
  | { type: 'MOVE'; index: number; dir: -1 | 1 }
  | { type: 'SELECT'; index: number | null }
  | { type: 'DRAG'; index: number; lat: number; lng: number }
  | { type: 'BEGIN_POINT'; altitude: number }
  | { type: 'BEGIN_ROUTE'; altitude: number }
  | { type: 'SET_SEED_ALTITUDE'; altitude: number };

function movedSelection(sel: number | null, index: number, dir: -1 | 1): number | null {
  if (sel === index) return index + dir;
  if (sel === index + dir) return index;
  return sel;
}

function removedSelection(sel: number | null, index: number): number | null {
  if (sel === null || sel === index) return null;
  return sel > index ? sel - 1 : sel;
}

function planReducer(state: PlanState, action: PlanAction): PlanState {
  switch (action.type) {
    case 'RESET':
      return INITIAL_PLAN;
    case 'CLEAR':
      return {
        ...state,
        waypoints: [],
        selectedIndex: null,
        planningKind: 'idle',
      };
    case 'FINISH_PLANNING':
      return { ...state, planningKind: 'idle' };
    case 'REPLACE':
      return {
        ...state,
        waypoints: action.waypoints,
        selectedIndex: action.waypoints.length > 0 ? 0 : null,
      };
    case 'ADD':
      if (state.planningKind === 'point') {
        return {
          ...state,
          waypoints: [createWaypoint(action.lat, action.lng, { alt: state.seedAltitude })],
          selectedIndex: 0,
        };
      }
      return {
        ...state,
        waypoints: [...state.waypoints, createWaypoint(action.lat, action.lng, { alt: state.seedAltitude })],
        selectedIndex: state.waypoints.length,
      };
    case 'ADD_NEXT': {
      const seed: LatLng =
        state.waypoints[state.waypoints.length - 1] ?? action.home ?? FALLBACK_SEED;
      return {
        ...state,
        waypoints: [
          ...state.waypoints,
          createWaypoint(seed.lat + ADD_OFFSET_DEG, seed.lng + ADD_OFFSET_DEG, {
            alt: state.seedAltitude,
          }),
        ],
        selectedIndex: state.waypoints.length,
      };
    }
    case 'INSERT_AFTER': {
      const current = state.waypoints[action.index];
      if (!current) return state;
      const next: LatLng = state.waypoints[action.index + 1] ?? action.home ?? current;
      const at = midpoint(current, next);
      return {
        ...state,
        waypoints: insertWaypointAt(
          state.waypoints,
          action.index + 1,
          createWaypoint(at.lat, at.lng, { alt: current.alt, speed: current.speed }),
        ),
        selectedIndex: action.index + 1,
      };
    }
    case 'UPDATE':
      return { ...state, waypoints: updateWaypointAt(state.waypoints, action.index, action.patch) };
    case 'REMOVE':
      return {
        ...state,
        waypoints: removeWaypointAt(state.waypoints, action.index),
        selectedIndex: removedSelection(state.selectedIndex, action.index),
      };
    case 'MOVE': {
      const j = action.index + action.dir;
      if (j < 0 || j >= state.waypoints.length) return state;
      return {
        ...state,
        waypoints: moveWaypoint(state.waypoints, action.index, action.dir),
        selectedIndex: movedSelection(state.selectedIndex, action.index, action.dir),
      };
    }
    case 'SELECT':
      return { ...state, selectedIndex: action.index };
    case 'DRAG':
      return {
        ...state,
        waypoints: updateWaypointAt(state.waypoints, action.index, {
          lat: action.lat,
          lng: action.lng,
        }),
        selectedIndex: action.index,
      };
    case 'BEGIN_POINT':
      return {
        ...state,
        waypoints: [],
        selectedIndex: null,
        planningKind: 'point',
        seedAltitude: action.altitude,
      };
    case 'BEGIN_ROUTE':
      return {
        ...state,
        waypoints: [],
        selectedIndex: null,
        planningKind: 'route',
        seedAltitude: action.altitude,
      };
    case 'SET_SEED_ALTITUDE':
      return { ...state, seedAltitude: action.altitude };
    default:
      return state;
  }
}

export interface UseDockMissionPlanArgs {
  /** Selected dock id - the plan resets whenever this changes. */
  dockId: string | null;
  /** Dock location (launch/home point). Null when the dock has no coordinates. */
  home: LatLng | null;
  /** Active keep-in geofences used for live validation. */
  keepInFences: KeepInFence[];
  /** Does this plan currently hold the map? Owned by the COP's map mode. */
  armed: boolean;
  /**
   * Ask for the map. The owner may refuse in effect — arming replaces, so
   * something else taking the map stands this plan down — which is why the
   * hook reads `armed` back rather than assuming its request stuck.
   */
  onArmedChange: (armed: boolean) => void;
}

export interface DockMissionPlanController {
  waypoints: PlanWaypoint[];
  selectedIndex: number | null;
  planOnMap: boolean;
  planningKind: 'idle' | 'point' | 'route';
  home: LatLng | null;
  violations: RouteViolation[];
  /** Waypoint indices with blocking errors (drives red map markers). */
  invalidIndices: Set<number>;
  totalDistanceM: number;
  estimatedTimeS: number;
  seedAltitude: number;
  /** Append a waypoint dropped on the map; selects it. */
  addFromMap: (lat: number, lng: number) => void;
  /** Append a waypoint from the form (seeded near the last point or home). */
  addWaypoint: () => void;
  /** Replace the whole route (e.g. loading a saved mission); disarms map planning. */
  replaceWaypoints: (waypoints: PlanWaypoint[]) => void;
  clear: () => void;
  finishPlanning: () => void;
  beginPoint: (altitude: number) => void;
  beginRoute: (altitude: number) => void;
  setSeedAltitude: (altitude: number) => void;
  /** Insert a midpoint waypoint between `index` and the next point. */
  insertAfter: (index: number) => void;
  updateAt: (index: number, patch: Partial<PlanWaypoint>) => void;
  removeAt: (index: number) => void;
  move: (index: number, dir: -1 | 1) => void;
  select: (index: number | null) => void;
  /** Live drag reposition from the map. */
  dragTo: (index: number, lat: number, lng: number) => void;
  stopPlanOnMap: () => void;
  togglePlanOnMap: () => void;
}

/**
 * Owns the draft dock-mission route shared by the form editor
 * (DockMissionPanel) and the COP map (DroneMap plan layers).
 */
export function useDockMissionPlan({
  dockId,
  home,
  keepInFences,
  armed,
  onArmedChange,
}: UseDockMissionPlanArgs): DockMissionPlanController {
  const [state, dispatch] = useReducer(planReducer, INITIAL_PLAN);

  // Read through a ref so the callbacks below stay stable: they are handed to
  // memoised children, and a new identity on every arm/disarm would churn them.
  const onArmedChangeRef = useRef(onArmedChange);
  onArmedChangeRef.current = onArmedChange;
  const armedRef = useRef(armed);
  armedRef.current = armed;
  const setArmed = useCallback((next: boolean) => onArmedChangeRef.current(next), []);

  // New dock = new plan. Clears the route and releases the map.
  useEffect(() => {
    dispatch({ type: 'RESET' });
    onArmedChangeRef.current(false);
  }, [dockId]);

  const addFromMap = useCallback(
    (lat: number, lng: number) => dispatch({ type: 'ADD', lat, lng }),
    [],
  );
  const addWaypoint = useCallback(() => dispatch({ type: 'ADD_NEXT', home }), [home]);
  const replaceWaypoints = useCallback(
    (waypoints: PlanWaypoint[]) => {
      dispatch({ type: 'REPLACE', waypoints });
      setArmed(false);
    },
    [setArmed],
  );
  const clear = useCallback(() => {
    dispatch({ type: 'CLEAR' });
    setArmed(false);
  }, [setArmed]);
  const finishPlanning = useCallback(() => {
    dispatch({ type: 'FINISH_PLANNING' });
    setArmed(false);
  }, [setArmed]);
  const beginPoint = useCallback(
    (altitude: number) => {
      dispatch({ type: 'BEGIN_POINT', altitude });
      setArmed(true);
    },
    [setArmed],
  );
  const beginRoute = useCallback(
    (altitude: number) => {
      dispatch({ type: 'BEGIN_ROUTE', altitude });
      setArmed(true);
    },
    [setArmed],
  );
  const setSeedAltitude = useCallback(
    (altitude: number) => dispatch({ type: 'SET_SEED_ALTITUDE', altitude }),
    [],
  );
  const insertAfter = useCallback(
    (index: number) => dispatch({ type: 'INSERT_AFTER', index, home }),
    [home],
  );
  const updateAt = useCallback(
    (index: number, patch: Partial<PlanWaypoint>) => dispatch({ type: 'UPDATE', index, patch }),
    [],
  );
  const removeAt = useCallback((index: number) => dispatch({ type: 'REMOVE', index }), []);
  const move = useCallback((index: number, dir: -1 | 1) => dispatch({ type: 'MOVE', index, dir }), []);
  const select = useCallback((index: number | null) => dispatch({ type: 'SELECT', index }), []);
  const dragTo = useCallback(
    (index: number, lat: number, lng: number) => dispatch({ type: 'DRAG', index, lat, lng }),
    [],
  );
  const stopPlanOnMap = useCallback(() => setArmed(false), [setArmed]);
  const togglePlanOnMap = useCallback(() => setArmed(!armedRef.current), [setArmed]);

  const violations = useMemo(
    () => validateRoute(state.waypoints, { home, keepInFences }),
    [state.waypoints, home, keepInFences],
  );
  const invalidIndices = useMemo(() => errorIndices(violations), [violations]);
  const totalDistanceM = useMemo(() => routeDistanceM(home, state.waypoints), [home, state.waypoints]);
  const estimatedTimeS = useMemo(
    () => estimateFlightTimeS(home, state.waypoints),
    [home, state.waypoints],
  );

  return {
    waypoints: state.waypoints,
    selectedIndex: state.selectedIndex,
    // Derived, never stored: the map mode is the single source of truth for
    // whether this plan is the thing a click means.
    planOnMap: armed,
    planningKind: state.planningKind,
    home,
    violations,
    invalidIndices,
    totalDistanceM,
    estimatedTimeS,
    seedAltitude: state.seedAltitude,
    addFromMap,
    addWaypoint,
    replaceWaypoints,
    clear,
    finishPlanning,
    beginPoint,
    beginRoute,
    setSeedAltitude,
    insertAfter,
    updateAt,
    removeAt,
    move,
    select,
    dragTo,
    stopPlanOnMap,
    togglePlanOnMap,
  };
}
